// Microsoft SSO Authentication Hook
// PBI 9.1: Single Sign-On (SSO) Integration - Microsoft OAuth
// Handles Microsoft authentication flow using MSAL

import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/msal';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export const useMicrosoftAuth = () => {
  const { instance } = useMsal();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = async (): Promise<AuthResponse> => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get token from Microsoft
      await instance.loginPopup(loginRequest);

      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["user.read", "email", "profile"],
        account: instance.getAllAccounts()[0],
      });

      // Send id_token to backend for validation
      const validateRes = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/sso/microsoft/validate-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenResponse.idToken}`
        },
        credentials: 'include',
      });

      const backendData = await validateRes.json();

      if (backendData.success) {
        setUser(backendData.user);
        return { success: true, user: backendData.user };
      } else {
        throw new Error(backendData.message || 'Backend authentication failed');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await instance.logoutPopup();
      setUser(null);
      
      // Clear backend cookie
      await fetch('http://localhost:3001/api/auth/microsoft/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Silent error handling
    }
  };

  return { user, loading, error, login, logout, isAuthenticated: !!user };
}; 